"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useMutation } from "@tanstack/react-query";
import { useCookies } from "react-cookie";
import { ArrowRight } from "lucide-react";
import { handleGetToken } from "@/api/auth";
import { ILoginFormData, validateIsEmail } from "@/hooks/types";
import BrandInput from "../inputs/BrandInput";
import { cn, display } from "@/lib/utils";

const LoginForm: React.FC = (): React.JSX.Element => {
  const router = useRouter();
  const [, setCookie] = useCookies(["access_token", "gender"]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [touched, setTouched] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationKey: ["getToken"],
    mutationFn: handleGetToken,
    onError: () => setServerError("Correo o contraseña no válidos. Revisa tus datos e inténtalo de nuevo."),
  });

  const emailError = touched && email !== "" && !validateIsEmail(email) ? "Escribe un correo válido" : undefined;
  const canSubmit = email !== "" && password !== "" && validateIsEmail(email) && !mutation.isPending;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    setServerError(null);
    const form: ILoginFormData = { email: email.trim(), password };
    mutation.mutate(form);
  };

  useEffect(() => {
    if (mutation.data?.access_token && mutation.isSuccess) {
      setCookie("access_token", mutation.data.access_token, {
        path: "/",
        expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        secure: true,
        sameSite: "strict",
      });
      router.push("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutation.isSuccess, router]);

  return (
    <form onSubmit={submit} noValidate className="text-brand-ink">
      <h1 className="mes-display text-[clamp(2rem,4.5vw,3rem)] leading-[0.95]">{display("Inicia sesión")}</h1>

      <div className="space-y-7 mt-12">
        <BrandInput
          label="Correo electrónico"
          type="email"
          value={email}
          onChange={(v) => {
            setEmail(v);
            setServerError(null);
          }}
          placeholder="tu@correo.com"
          autoComplete="email"
          error={emailError}
        />
        <BrandInput
          label="Contraseña"
          type="password"
          value={password}
          onChange={(v) => {
            setPassword(v);
            setServerError(null);
          }}
          placeholder="••••••••"
          autoComplete="current-password"
        />
      </div>

      <div className="mt-4">
        <Link href="/password-reset/email" className="mes-label opacity-60 hover:opacity-100 hover:text-brand-blue transition-colors">
          ¿Olvidaste tu contraseña?
        </Link>
      </div>

      {serverError && (
        <div role="alert" className="mt-6 border border-brand-red bg-brand-red/5 px-4 py-3 mes-label text-brand-red">
          {serverError}
        </div>
      )}

      <button
        type="submit"
        disabled={!canSubmit}
        className={cn(
          "mt-8 w-full flex items-center justify-between mes-label px-5 py-4 transition-colors",
          "bg-brand-ink text-white hover:bg-brand-blue",
          "disabled:bg-brand-ink/20 disabled:text-brand-ink/50 disabled:cursor-not-allowed"
        )}
      >
        <span>{mutation.isPending ? "Entrando…" : "Entrar"}</span>
        <ArrowRight className={cn("w-4 h-4", mutation.isPending && "animate-pulse")} />
      </button>

      <p className="mes-label mt-10 opacity-60">
        ¿Aún no tienes cuenta?{" "}
        <Link href="/register" className="text-brand-blue hover:underline">
          Regístrate →
        </Link>
      </p>
    </form>
  );
};

export default LoginForm;
